// Organizer game management (UC-14/15/16): edit details, remove players,
// and cancel the game. Only reachable by the organizer.

import $ from 'jquery';
import type { Court } from '@shared/models';
import type { GameFormat, GameVisibility, SkillLevel } from '@shared/models';
import type { GameDetail } from '@shared/dto';
import type { Screen } from './types';
import { api } from '../api';
import { navigate, refresh } from '../router';
import { avatarHtml, esc } from '../ui/dom';
import { FORMAT_TR, SKILL_TR } from '../ui/format';
import { emptyHtml, loadingHtml } from '../ui/states';
import { toast } from '../ui/toast';

const FORMATS: GameFormat[] = ['3v3', '4v4', '5v5', 'pickup'];
const SKILLS: SkillLevel[] = ['beginner', 'intermediate', 'advanced'];

/** ISO instant -> value for <input type="datetime-local"> in local time. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

function rosterHtml(d: GameDetail): string {
  // The organizer manages their own roster, so every member is revealed (never masked).
  return d.roster
    .map(({ user: p, relation }) => {
      const action =
        relation === 'organizer'
          ? '<span class="badge badge--accent">Organizatör</span>'
          : `<button class="btn btn--danger btn--sm" data-remove="${esc(p.id)}">Çıkar</button>`;
      return `<div class="roster__row">
        ${avatarHtml(p.name, p.avatarUrl, 36)}
        <span class="roster__name">${esc(p.name)}</span>
        ${action}
      </div>`;
    })
    .join('');
}

export const ManageScreen: Screen = async (body, params) => {
  body.html(loadingHtml());
  const d = await api.call('games.get', { gameId: params.id });

  if (!d.viewerIsOrganizer) {
    body.html(emptyHtml('🔒', 'Bu oyunu yönetme yetkin yok.'));
    return;
  }
  if (d.game.status === 'cancelled') {
    body.html(emptyHtml('❌', 'Bu oyun iptal edilmiş.'));
    return;
  }

  const courts = await api.call('courts.list', undefined);
  const courtOpts = courts
    .map(
      (c: Court) =>
        `<option value="${esc(c.id)}" ${c.id === d.court.id ? 'selected' : ''}>${esc(c.name)} · ${esc(c.area)}</option>`,
    )
    .join('');
  const formatChips = FORMATS.map(
    (f) => `<button type="button" class="chip ${f === d.game.format ? 'is-on' : ''}" data-format="${f}">${FORMAT_TR[f]}</button>`,
  ).join('');
  const skillOpts = SKILLS.map(
    (s) => `<option value="${s}" ${s === d.game.skill ? 'selected' : ''}>${SKILL_TR[s]}</option>`,
  ).join('');

  body.html(`
    <div class="manage">
      <label class="field">
        <span class="field__label">Saha</span>
        <select class="select" id="m-court">${courtOpts}</select>
      </label>

      <label class="field">
        <span class="field__label">Tarih ve saat</span>
        <input class="input" id="m-when" type="datetime-local" value="${toLocalInput(d.game.startsAt)}" />
      </label>

      <div class="field">
        <span class="field__label">Format</span>
        <div class="chips" id="m-formats">${formatChips}</div>
      </div>

      <label class="field">
        <span class="field__label">Seviye</span>
        <select class="select" id="m-skill">${skillOpts}</select>
      </label>

      <label class="field">
        <span class="field__label">Kadro (kaç kişi)</span>
        <input class="input" id="m-capacity" type="number" min="${d.roster.length}" max="12" value="${d.game.capacity}" />
      </label>

      <div class="field">
        <span class="field__label">Görünürlük</span>
        <div class="vis" id="m-visibility">
          <button type="button" class="vis__opt ${d.game.visibility === 'private' ? 'is-on' : ''}" data-vis="private">
            <span class="vis__title">🔒 Sadece davetliler</span>
            <span class="vis__desc">Yalnızca davet ettiğin kişiler görür ve katılır.</span>
          </button>
          <button type="button" class="vis__opt ${d.game.visibility === 'public' ? 'is-on' : ''}" data-vis="public">
            <span class="vis__title">🌐 Herkese açık</span>
            <span class="vis__desc">Yakındaki herkes bulabilir ve katılabilir.</span>
          </button>
        </div>
      </div>

      <button class="btn btn--primary btn--block" id="m-save">Değişiklikleri kaydet</button>

      <div class="section-title">Kadro (${d.roster.length}/${d.game.capacity})</div>
      <div class="roster" id="m-roster">${rosterHtml(d)}</div>

      <div class="section-title">Tehlikeli bölge</div>
      <button class="btn btn--danger btn--block" id="m-cancel">Oyunu iptal et</button>
    </div>
  `);

  let format: GameFormat = d.game.format;
  body.on('click', '#m-formats .chip', function () {
    format = $(this).data('format') as GameFormat;
    $('#m-formats .chip').removeClass('is-on');
    $(this).addClass('is-on');
  });

  let visibility: GameVisibility = d.game.visibility;
  body.on('click', '#m-visibility .vis__opt', function () {
    visibility = $(this).data('vis') as GameVisibility;
    $('#m-visibility .vis__opt').removeClass('is-on');
    $(this).addClass('is-on');
  });

  $('#m-save').on('click', async () => {
    const whenLocal = $('#m-when').val() as string;
    const capacity = Math.max(d.roster.length, Math.min(12, Number($('#m-capacity').val()) || d.game.capacity));
    try {
      await api.call('games.update', {
        gameId: d.game.id,
        patch: {
          courtId: $('#m-court').val() as string,
          startsAt: whenLocal ? new Date(whenLocal).toISOString() : d.game.startsAt,
          format,
          skill: $('#m-skill').val() as SkillLevel,
          capacity,
          visibility,
        },
      });
      toast('Değişiklikler kaydedildi', 'success');
      navigate(`/game/${d.game.id}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  body.on('click', '[data-remove]', async function () {
    const playerId = $(this).data('remove') as string;
    try {
      await api.call('games.removePlayer', { gameId: d.game.id, playerId });
      toast('Oyuncu çıkarıldı', 'info');
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  $('#m-cancel').on('click', async () => {
    if (!confirm('Oyunu iptal etmek istediğine emin misin?')) return;
    await api.call('games.cancel', { gameId: d.game.id });
    toast('Oyun iptal edildi', 'info');
    navigate('/home');
  });
};
